import React, { useState, useEffect, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { ReusableTableColumn } from "../../@types/table";
import { useNavigate } from "react-router-dom";
import { Loading } from "../common/Table/Loading";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

interface TableProps {
  columns: ReusableTableColumn[];
  data: Array<Record<string, any>>;
  hasActions?: boolean;
  loadMore?: (direction: "up" | "down") => Promise<void>;
  modules?: string;
  isLoading: boolean;
  isLoadingFilter?: boolean;
  isFetchingMore?: boolean;
  renderActionMenu?: (
    row: Record<string, any>,
    toggleVisibility: () => void,
    isVisible: boolean,
  ) => React.ReactNode;
}

const TableSkeleton = ({
  colCount,
  rowsCount,
}: {
  colCount: number;
  rowsCount: number;
}) => {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          {Array.from({ length: colCount }).map((_, i) => (
            <th key={i} className="p-3">
              <Skeleton height={20} width="80%" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rowsCount }).map((_, rowIndex) => (
          <tr key={rowIndex} className="border-b">
            {Array.from({ length: colCount }).map((_, colIndex) => (
              <td key={colIndex} className="px-4 py-3">
                <Skeleton height={16} width="80%" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ReusableTable: React.FC<TableProps> = ({
  columns,
  data,
  hasActions = false,
  loadMore,
  isLoading,
  renderActionMenu,
  isLoadingFilter,
  isFetchingMore,
  modules,
}) => {
  const navigate = useNavigate();
  const [isFetching, setIsFetching] = useState(false);
  const [activeRow, setActiveRow] = useState<number | null>(null);

  const [ref, inView] = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && !isLoading && loadMore) {
      setIsFetching(true);
      loadMore("down");
      setIsFetching(false);
    }
  }, [inView, isLoading, loadMore]);

  const toggleRowVisibility = (index: number) => {
    setActiveRow((prev) => (prev === index ? null : index));
  };

  const handleRowClick = (id: any) => {
    if (modules) {
    }
  };

  return (
    <div className="w-full overflow-x-auto h-full">
      <table className="table-auto w-full  border-collapse">
        <thead className="bg-gray-50 ">
          <tr>
            {columns.map((column) => (
              <th
                style={{ width: `${column.width}%` }}
                key={column.key}
                className={`p-3 text-left text-sm font-medium text-[#1D2939] ${column.className} `}
              >
                {column.label}
              </th>
            ))}
            {hasActions && (
              <th className="p-3 text-left text-sm font-medium text-gray-600">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoadingFilter && <Loading data={columns} />}

          {isLoadingFilter || (
            <>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (hasActions ? 1 : 0)}
                    className="px-4 py-2 text-center text-gray-500"
                  >
                    <SkeletonTheme>
                      <TableSkeleton colCount={5} rowsCount={6} />
                    </SkeletonTheme>
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={index}
                    className={`bg-white border-y border-gray-200 first:border-t-transparent last:border-b-transparent ${
                      index === data.length - 1 ? "border-b-0" : ""
                    }`}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        onClick={() => handleRowClick(row.id)}
                        key={column.key}
                        className={`px-3 py-[18px] text-sm text-[#1D2939] ${column.bodyClassName} ${
                          colIndex === 0 && "text-start"
                        }`}
                      >
                        {row[column.key]}
                      </td>
                    ))}
                    {hasActions && (
                      <td className="px-4 py-2 text-left relative">
                        {renderActionMenu && activeRow === index ? (
                          renderActionMenu(
                            row,
                            () => toggleRowVisibility(index),
                            true,
                          )
                        ) : (
                          // Render inline buttons (Scenario 1) if no visibility toggle is used
                          <div className="flex items-center gap-4">
                            {renderActionMenu &&
                              renderActionMenu(
                                row,
                                () => toggleRowVisibility(index),
                                false,
                              )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </>
          )}
          {isLoading && <Loading data={columns} />}
          <tr ref={ref}>
            <td
              colSpan={columns.length + (hasActions ? 1 : 0)}
              className="h-1"
            ></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export { ReusableTable };
